import React from 'react';
import { PageLayout } from '@/shared/layout';
import { useAuth } from '@/core/auth/AuthContext';
import { Card, Badge, DonutLegendChart } from '@/components/ui';
import { useUserRoleAssignments } from '@/core/roles/useUserRoleAssignments';
import AdminDashboardPage from '@/modules/admin/dashboard/DashboardPage';

export default function UnifiedDashboardPage() {
  const { profile, user } = useAuth();
  const { assignments } = useUserRoleAssignments(user?.id ?? null);

  const displayRole = assignments.length > 0 
    ? [...new Set(assignments.map(a => a.roleName))].join(' - ')
    : (profile?.role_name || 'Tổ trưởng thi công');

  if (profile?.role_code === 'super_admin') {
    return <AdminDashboardPage />;
  }

  // MOCK DATA: Theo yêu cầu xây dựng UI tĩnh khớp với bản vẽ
  const mockProgressCategories = [
    { name: 'Cây xanh', progress: 75, color: 'bg-emerald-600' },
    { name: 'Cảnh quan mặt cỏ', progress: 60, color: 'bg-emerald-600' },
    { name: 'Hệ thống tưới', progress: 80, color: 'bg-emerald-600' },
    { name: 'Lối đi, sân bãi', progress: 45, color: 'bg-amber-500' },
    { name: 'Hệ thống chiếu sáng', progress: 30, color: 'bg-amber-500' },
    { name: 'Tiểu cảnh - Hồ nước', progress: 20, color: 'bg-orange-500' },
  ];

  const mockSchedule = [
    { time: '07:30 - 09:00', task: 'Trồng cây bụi, cây nền', zone: 'Khu A - Tuyến 1', status: 'Hoàn thành', badge: 'success' },
    { time: '09:00 - 11:00', task: 'Trải đất màu, san nền', zone: 'Khu A - Tuyến 2', status: 'Hoàn thành', badge: 'success' },
    { time: '13:30 - 15:30', task: 'Lắp đặt hệ thống tưới', zone: 'Khu A - Tuyến 3', status: 'Đang thực hiện', badge: 'primary' },
    { time: '15:30 - 17:00', task: 'Lắp đặt đèn chiếu sáng', zone: 'Khu A - Tuyến 3', status: 'Chờ thực hiện', badge: 'warning' },
    { time: '17:00 - 18:00', task: 'Vệ sinh, dọn dẹp hiện trường', zone: 'Toàn tuyến', status: 'Chờ thực hiện', badge: 'warning' },
  ];

  const mockImages = [
    { time: '08:15', src: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=300' },
    { time: '08:30', src: 'https://images.unsplash.com/photo-1558904541-efa843a96f0f?auto=format&fit=crop&q=80&w=300' },
    { time: '08:45', src: 'https://images.unsplash.com/photo-1598902108854-10e335adac99?auto=format&fit=crop&q=80&w=300' },
    { time: '10:20', src: 'https://images.unsplash.com/photo-1592424001807-f6558fa6c9a9?auto=format&fit=crop&q=80&w=300' },
    { time: '13:40', src: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=300' },
    { time: '14:10', src: 'https://images.unsplash.com/photo-1558904541-efa843a96f0f?auto=format&fit=crop&q=80&w=300' },
  ];

  const mockDelayedTasks = [
    { task: 'Lắp đặt hệ thống thoát nước', zone: 'Khu A - Tuyến 2', delay: 'Quá 1 ngày' },
    { task: 'Trồng cây bóng mát', zone: 'Khu A - Tuyến 3', delay: 'Quá 2 ngày' },
    { task: 'Lắp đặt đèn sân vườn', zone: 'Khu A - Tuyến 1', delay: 'Quá 3 ngày' },
  ];

  const personnelData = [
    { name: 'Có mặt', value: 10, color: '#059669' },
    { name: 'Nghỉ phép', value: 1, color: '#3b82f6' },
    { name: 'Nghỉ ốm', value: 1, color: '#f59e0b' },
    { name: 'Vắng', value: 2, color: '#cbd5e1' },
  ];

  const mockMaterials = [
    { name: 'Đất trồng', used: '2.5 m³', remain: '17.5 m³' },
    { name: 'Phân hữu cơ', used: '50 kg', remain: '150 kg' },
    { name: 'Cây bụi', used: '35 cây', remain: '65 cây' },
    { name: 'Cỏ nhung', used: '120 m²', remain: '380 m²' },
    { name: 'Ống LDPE 20mm', used: '80 m', remain: '120 m' },
  ];

  const mockEquipments = [
    { name: 'Máy đào mini', code: 'KOMATSU PC30', status: 'Đang sử dụng', badge: 'primary' },
    { name: 'Máy trộn bê tông', code: 'HONG HA 250L', status: 'Đang sử dụng', badge: 'primary' },
    { name: 'Máy cắt cỏ', code: 'STIHL FS 250', status: 'Đang sử dụng', badge: 'primary' },
    { name: 'Xe rùa', code: 'Wheel Barrow', status: 'Sẵn sàng', badge: 'success' },
  ];

  const mockDocuments = [
    { name: 'Biện pháp thi công cảnh quan Khu A.pdf', date: '15/05/2024', icon: 'ri-file-pdf-2-line', color: 'text-red-500' },
    { name: 'Bản vẽ hệ thống tưới tự động.dwg', date: '15/05/2024', icon: 'ri-file-edit-line', color: 'text-blue-500' },
    { name: 'Tiến độ tổng thể dự án.xlsx', date: '14/05/2024', icon: 'ri-file-excel-2-line', color: 'text-emerald-500' },
  ];

  return (
    <PageLayout
      title="Tổng quan thi công"
      description={`Xin chào, ${profile?.full_name || 'Nguyễn Văn An'} (${displayRole})`}
    >
      <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-500">
        
        {/* Row 1: KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card className="p-3 space-y-2 rounded-md">
            <div className="flex items-center gap-2">
              <div className="bg-emerald-50 text-emerald-600 p-2 rounded-md">
                <i className="ri-task-line text-lg" />
              </div>
              <div>
                <p className="sm:text-sm text-xs text-slate-500">Công việc hôm nay</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl text-slate-900">7</span>
                  <span className="sm:text-sm text-xs text-slate-500">Nhiệm vụ</span>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between sm:text-sm text-xs">
              <span className="text-emerald-600">5 đã hoàn thành</span>
              <span className="text-slate-500">71%</span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-600 rounded-full" style={{ width: '71%' }} />
            </div>
          </Card>

          <Card className="p-3 space-y-2 rounded-md">
            <div className="flex items-center gap-2">
              <div className="bg-blue-50 text-blue-600 p-2 rounded-md">
                <i className="ri-calendar-event-line text-lg" />
              </div>
              <div>
                <p className="sm:text-sm text-xs text-slate-500">Công việc tuần này</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl text-slate-900">24</span>
                  <span className="sm:text-sm text-xs text-slate-500">Nhiệm vụ</span>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100 sm:text-sm text-xs text-slate-600">
              Đã hoàn thành 16 (67%)
            </div>
          </Card>

          <Card className="p-3 space-y-2 rounded-md">
            <div className="flex items-center gap-2">
              <div className="bg-orange-50 text-orange-600 p-2 rounded-md">
                <i className="ri-time-line text-lg" />
              </div>
              <div>
                <p className="sm:text-sm text-xs text-slate-500">Công việc quá hạn</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl text-slate-900">3</span>
                  <span className="sm:text-sm text-xs text-slate-500">Nhiệm vụ</span>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between sm:text-sm text-xs text-red-600">
              <span>Cần xử lý ngay</span>
              <i className="ri-arrow-right-line" />
            </div>
          </Card>

          <Card className="p-3 space-y-2 rounded-md">
            <div className="flex items-center gap-2">
              <div className="bg-purple-50 text-purple-600 p-2 rounded-md">
                <i className="ri-team-line text-lg" />
              </div>
              <div>
                <p className="sm:text-sm text-xs text-slate-500">Nhân sự trong tổ</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl text-slate-900">12/14</span>
                  <span className="sm:text-sm text-xs text-slate-500">Người</span>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100 sm:text-sm text-xs text-slate-600">
              Có mặt: 10 / 12
            </div>
          </Card>

          <Card className="p-3 space-y-2 rounded-md">
            <div className="flex items-center gap-2">
              <div className="bg-cyan-50 text-cyan-600 p-2 rounded-md">
                <i className="ri-dashboard-line text-lg" />
              </div>
              <div>
                <p className="sm:text-sm text-xs text-slate-500">Tiến độ tổng thể</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl text-slate-900">68%</span>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100 sm:text-sm text-xs text-emerald-600 flex items-center gap-1">
              <i className="ri-arrow-up-line" />
              <span>12% so với tuần trước</span>
            </div>
          </Card>

          <Card className="p-3 space-y-2 rounded-md">
            <div className="flex items-center gap-2">
              <div className="bg-emerald-50 text-emerald-600 p-2 rounded-md">
                <i className="ri-shield-check-line text-lg" />
              </div>
              <div>
                <p className="sm:text-sm text-xs text-slate-500">An toàn</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl text-slate-900">0</span>
                  <span className="sm:text-sm text-xs text-slate-500">Sự cố</span>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100 sm:text-sm text-xs text-slate-500">
              30 ngày qua
            </div>
          </Card>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Card className="p-3 flex flex-col h-[320px] rounded-md">
            <div className="flex justify-between items-center mb-3">
              <h3 className="sm:text-sm text-xs text-slate-900">Tiến độ thi công theo hạng mục</h3>
              <button className="sm:text-sm text-xs text-emerald-600">Xem chi tiết</button>
            </div>
            <div className="space-y-4 flex-1 overflow-y-auto pr-2">
              {mockProgressCategories.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 sm:text-sm text-xs">
                  <span className="w-32 text-slate-700 truncate">{item.name}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.progress}%` }} />
                  </div>
                  <span className="w-8 text-right text-slate-600">{item.progress}%</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-3 flex flex-col h-[320px] rounded-md">
            <div className="flex justify-between items-center mb-3">
              <h3 className="sm:text-sm text-xs text-slate-900">Lịch thi công hôm nay (15/05/2024)</h3>
              <button className="sm:text-sm text-xs text-emerald-600">Xem tất cả</button>
            </div>
            <div className="space-y-3 flex-1 overflow-y-auto relative pl-2">
              <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-100" />
              {mockSchedule.map((item, idx) => (
                <div key={idx} className="flex gap-3 relative">
                  <div className={`w-2 h-2 mt-1.5 rounded-full z-10 ${item.badge === 'success' ? 'bg-emerald-500' : item.badge === 'primary' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                  <div className="flex-1 flex justify-between items-start">
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">{item.time}</p>
                      <p className="sm:text-sm text-xs text-slate-900">{item.task}</p>
                      <p className="text-xs text-slate-400 italic">{item.zone}</p>
                    </div>
                    <Badge variant={item.badge as any}>{item.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-3 flex flex-col h-[320px] rounded-md">
            <div className="flex justify-between items-center mb-3">
              <h3 className="sm:text-sm text-xs text-slate-900">Ảnh hiện trường cập nhật</h3>
              <button className="sm:text-sm text-xs text-emerald-600">Xem tất cả</button>
            </div>
            <div className="grid grid-cols-2 gap-2 flex-1 overflow-y-auto">
              {mockImages.map((img, idx) => (
                <div key={idx} className="relative rounded-md overflow-hidden bg-slate-100 group">
                  <img src={img.src} alt="Hiện trường" className="w-full h-24 object-cover transition-transform group-hover:scale-105" />
                  <div className="absolute bottom-1 left-1 bg-white/90 text-xs px-1.5 py-0.5 rounded text-slate-700">
                    {img.time}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <Card className="p-3 flex flex-col min-h-[250px] rounded-md">
            <div className="flex justify-between items-center mb-3">
              <h3 className="sm:text-sm text-xs text-slate-900">Công việc quá hạn</h3>
              <button className="sm:text-sm text-xs text-emerald-600">Xem tất cả</button>
            </div>
            <div className="space-y-3 flex-1">
              {mockDelayedTasks.map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start p-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-colors">
                  <div className="bg-red-50 text-red-500 p-1.5 rounded-md mt-0.5">
                    <i className="ri-alarm-warning-line text-sm" />
                  </div>
                  <div className="flex-1 space-y-0.5">
                    <p className="sm:text-sm text-xs text-slate-800">{item.task}</p>
                    <p className="text-xs text-slate-400 italic">{item.zone}</p>
                  </div>
                  <span className="text-xs text-red-500 whitespace-nowrap">{item.delay}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-3 flex flex-col min-h-[250px] rounded-md">
            <div className="mb-1">
              <h3 className="sm:text-sm text-xs text-slate-900">Nhân sự tổ</h3>
            </div>
            <div className="flex-1 -mx-2">
              <DonutLegendChart
                title=""
                centerValue={12}
                centerLabel="Tổng số"
                data={personnelData}
                total={14}
                showLegendPercent={true}
                className="h-[180px]"
              />
            </div>
            <button className="sm:text-sm text-xs text-emerald-600 flex items-center gap-1 mt-2">
              <i className="ri-eye-line" /> Xem chi tiết nhân sự
            </button>
          </Card>

          <Card className="p-3 flex flex-col min-h-[250px] rounded-md">
            <div className="flex justify-between items-center mb-3">
              <h3 className="sm:text-sm text-xs text-slate-900">Vật tư sử dụng hôm nay</h3>
              <button className="sm:text-sm text-xs text-emerald-600">Xem chi tiết</button>
            </div>
            <div className="flex-1 overflow-x-auto">
              <table className="w-full sm:text-sm text-xs text-left">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-100">
                    <th className="py-2 font-normal">Vật tư</th>
                    <th className="py-2 text-right font-normal">Đã dùng</th>
                    <th className="py-2 text-right font-normal">Còn lại</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {mockMaterials.map((item, idx) => (
                    <tr key={idx}>
                      <td className="py-2 text-slate-800">{item.name}</td>
                      <td className="py-2 text-right text-slate-600">{item.used}</td>
                      <td className="py-2 text-right text-slate-600">{item.remain}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-3 flex flex-col min-h-[250px] rounded-md">
            <div className="flex justify-between items-center mb-3">
              <h3 className="sm:text-sm text-xs text-slate-900">Thiết bị đang sử dụng</h3>
              <button className="sm:text-sm text-xs text-emerald-600">Xem chi tiết</button>
            </div>
            <div className="space-y-3 flex-1 overflow-y-auto pr-1">
              {mockEquipments.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-400">
                    <i className="ri-truck-line text-lg" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <p className="sm:text-sm text-xs text-slate-800 truncate">{item.name}</p>
                    <p className="text-xs text-slate-400 truncate italic">{item.code}</p>
                  </div>
                  <Badge variant={item.badge as any} className="whitespace-nowrap">{item.status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Row 4 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Card className="p-3 min-h-[120px] rounded-md">
            <h3 className="sm:text-sm text-xs text-slate-900 mb-3">Thông báo</h3>
            <div className="flex gap-3 items-start bg-amber-50/50 p-2 rounded-md">
              <i className="ri-notification-3-line text-amber-500 mt-0.5" />
              <div className="flex-1 space-y-0.5">
                <p className="sm:text-sm text-xs text-slate-800">Họp giao ban tổ thi công vào 07:00 sáng mai (16/05/2024) tại lán trại công trường.</p>
                <p className="text-xs text-slate-400">2 giờ trước</p>
              </div>
            </div>
            <button className="sm:text-sm text-xs text-emerald-600 flex items-center gap-1 mt-3">
              Xem tất cả thông báo <i className="ri-arrow-right-line" />
            </button>
          </Card>

          <Card className="p-3 min-h-[120px] rounded-md">
            <h3 className="sm:text-sm text-xs text-slate-900 mb-3">Tài liệu - Bản vẽ mới cập nhật</h3>
            <div className="space-y-2">
              {mockDocuments.map((doc, idx) => (
                <div key={idx} className="flex justify-between items-center sm:text-sm text-xs group cursor-pointer hover:bg-slate-50 p-1 -mx-1 rounded-md transition-colors">
                  <div className="flex items-center gap-2 truncate pr-2">
                    <i className={`${doc.icon} ${doc.color}`} />
                    <span className="text-slate-700 truncate group-hover:text-emerald-600 transition-colors">{doc.name}</span>
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap">{doc.date}</span>
                </div>
              ))}
            </div>
            <button className="sm:text-sm text-xs text-emerald-600 flex items-center gap-1 mt-3">
              Xem tất cả tài liệu <i className="ri-arrow-right-line" />
            </button>
          </Card>

          <Card className="p-3 min-h-[120px] flex flex-col justify-between rounded-md">
            <div className="flex justify-between items-center mb-2">
              <h3 className="sm:text-sm text-xs text-slate-900">An toàn lao động</h3>
              <button className="sm:text-sm text-xs text-emerald-600">Xem chi tiết</button>
            </div>
            <p className="text-xs text-slate-500 text-center mb-4">Không có sự cố an toàn trong 30 ngày qua</p>
            <div className="flex justify-between items-center px-2">
              <div className="text-center space-y-1">
                <i className="ri-shield-cross-line text-emerald-500 text-lg" />
                <p className="text-lg text-slate-900">0</p>
                <p className="text-xs text-slate-500">Sự cố</p>
              </div>
              <div className="text-center space-y-1">
                <i className="ri-hospital-line text-orange-500 text-lg" />
                <p className="text-lg text-slate-900">0</p>
                <p className="text-xs text-slate-500">Tai nạn</p>
              </div>
              <div className="text-center space-y-1">
                <i className="ri-eye-2-line text-blue-500 text-lg" />
                <p className="text-lg text-slate-900">10</p>
                <p className="text-xs text-slate-500">Kiểm tra</p>
              </div>
              <div className="text-center space-y-1">
                <i className="ri-checkbox-circle-line text-emerald-500 text-lg" />
                <p className="text-lg text-slate-900">100%</p>
                <p className="text-xs text-slate-500">Tuân thủ</p>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </PageLayout>
  );
}
